/**
 * Public parent packet — token auth. PDF + per-form parent fields; submit saves form_data.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Paper,
  Alert,
  CircularProgress,
  TextField,
  FormControlLabel,
  Checkbox,
  Divider,
} from '@mui/material';
import { API_BASE_URL } from '../services/api';

/** form_1 / form_01 → 1 */
function formIdToNum(formId) {
  const m = /^form_(\d+)$/i.exec(formId || '');
  return m ? parseInt(m[1], 10) : -1;
}

function isInformationalPacketForm(formId) {
  const n = formIdToNum(formId);
  return (n >= 1 && n <= 6) || n === 16 || n === 17;
}

/** Full letter text (API form_text); forms 01–04, 06 */
function informationalTextOnlyForm(formId) {
  const n = formIdToNum(formId);
  return [1, 2, 3, 4, 6].includes(n);
}

/** Calendar + OCFS-6029 + meals application PDFs */
function informationalPdfForm(formId) {
  const n = formIdToNum(formId);
  return n === 5 || n === 16 || n === 17;
}

const PDF_INFO_HINTS = {
  5: 'The school calendar below shows off days and daily schedule.',
  16: 'Review the allergy emergency plan (OCFS-6029) below, then continue.',
  17: 'Review the meals application below, then continue.',
};

function initialFieldsForForm(formId, packet) {
  const p = packet || {};
  const base = {
    confirm_agreed: false,
  };
  switch (formId) {
    case 'form_07':
      return {
        ...base,
        mother_name: p.mother_name || '',
        mother_cell: p.mother_cell || '',
        father_name: p.father_name || '',
        father_cell: p.father_cell || '',
        home_address: p.home_address || '',
        email: p.parent_email || '',
        allergies: p.allergies || '',
        medication_school: '',
        medication_home: '',
        ec1_name: '',
        ec1_relationship: '',
        ec1_phone: '',
        ec2_name: '',
        ec2_relationship: '',
        ec2_phone: '',
        pediatrician_name: '',
        pediatrician_phone: '',
        dentist_name: '',
        dentist_phone: '',
        preferred_hospital: '',
        insurance_id: '',
        insurance_type: '',
      };
    case 'form_08':
      return {
        ...base,
        visual_impairment: false,
        glasses: false,
        ear_infection: false,
        ear_tubes: false,
        hearing_impairment: false,
        seizure: false,
        gtube: false,
        asthma: false,
        respiratory_compromise: false,
        cardiac_compromise: false,
        skin_condition: false,
        dental_condition: false,
        medications: '',
        other_medical: '',
      };
    case 'form_09':
      return {
        ...base,
        allergy_rows: Array(5).fill(null).map(() => ({
          allergen: '',
          eat: false,
          touch: false,
          smell: false,
          reaction: '',
        })),
        food_from_home: '',
        liquid_type: '',
      };
    case 'form_10':
      return {
        ...base,
        nickname: p.student_nickname || '',
        hair_color: p.student_hair_color || '',
        eye_color: p.student_eye_color || '',
        glasses: '',
        physical_description: '',
        usual_behavior: '',
        communication_ability: '',
        favorite_locations: '',
      };
    case 'form_11':
    case 'form_12':
    case 'form_18':
    case 'form_19':
      return { ...base };
    case 'form_13':
      return {
        ...base,
        c_classroom: false,
        c_newsletters: false,
        c_hallways: false,
        c_website: false,
        c_flyers: false,
        c_slideshow_building: false,
        c_class_list: false,
        c_yearbook: false,
        comm_sms: false,
        comm_email: false,
        comm_voice: false,
        comm_data_rates_ack: false,
      };
    case 'form_14':
      return {
        ...base,
        p1_name: '',
        p1_phone: '',
        p2_name: '',
        p2_phone: '',
        p3_name: '',
        p3_phone: '',
      };
    case 'form_15':
      return {
        ...base,
        address: p.home_address || '',
      };
    default:
      return { ...base };
  }
}

function FormFields({ formId, fields, setF, packet }) {
  const row = (label, key, props = {}) => (
    <TextField
      key={key}
      fullWidth
      label={label}
      value={fields[key] ?? ''}
      onChange={(e) => setF(key, e.target.value)}
      margin="normal"
      size="small"
      {...props}
    />
  );
  const cb = (label, key) => (
    <FormControlLabel
      key={key}
      control={<Checkbox checked={!!fields[key]} onChange={(e) => setF(key, e.target.checked)} />}
      label={label}
    />
  );

  if (formId === 'form_07') {
    return (
      <>
        <Typography variant="subtitle2" sx={{ mt: 1 }}>Child (from school record)</Typography>
        <Typography variant="body2" color="text.secondary">
          {packet.student_first_name} {packet.student_last_name} — DOB {packet.student_dob || '—'}
        </Typography>
        <Divider sx={{ my: 2 }} />
        {row('Mother/Guardian name', 'mother_name')}
        {row('Mother cell', 'mother_cell')}
        {row('Father/Guardian name', 'father_name')}
        {row('Father cell', 'father_cell')}
        {row('Home address', 'home_address', { multiline: true, minRows: 2 })}
        {row('Email', 'email')}
        {row('Allergies', 'allergies', { multiline: true, minRows: 2 })}
        {row('Medication at school', 'medication_school')}
        {row('Medication at home', 'medication_home')}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Emergency contact 1</Typography>
        {row('Name', 'ec1_name')}
        {row('Relationship', 'ec1_relationship')}
        {row('Phone', 'ec1_phone')}
        <Typography variant="subtitle2" sx={{ mt: 2 }}>Emergency contact 2</Typography>
        {row('Name', 'ec2_name')}
        {row('Relationship', 'ec2_relationship')}
        {row('Phone', 'ec2_phone')}
        {row('Pediatrician name', 'pediatrician_name')}
        {row('Pediatrician phone', 'pediatrician_phone')}
        {row('Dentist name', 'dentist_name')}
        {row('Dentist phone', 'dentist_phone')}
        {row('Preferred hospital', 'preferred_hospital')}
        {row('Insurance ID', 'insurance_id')}
        {row('Insurance type', 'insurance_type')}
      </>
    );
  }
  if (formId === 'form_08') {
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {packet.student_first_name} {packet.student_last_name} — DOB {packet.student_dob || '—'}
        </Typography>
        {cb('Visual impairment', 'visual_impairment')}
        {cb('Glasses', 'glasses')}
        {cb('History of ear infection', 'ear_infection')}
        {cb('Ear tubes', 'ear_tubes')}
        {cb('Hearing impairment', 'hearing_impairment')}
        {cb('Seizure history', 'seizure')}
        {cb('G-tube', 'gtube')}
        {cb('Asthma', 'asthma')}
        {cb('Respiratory compromise', 'respiratory_compromise')}
        {cb('Cardiac compromise', 'cardiac_compromise')}
        {cb('Skin condition', 'skin_condition')}
        {cb('Dental condition', 'dental_condition')}
        <TextField
          fullWidth
          label="Medications"
          value={fields.medications || ''}
          onChange={(e) => setF('medications', e.target.value)}
          margin="normal"
          size="small"
          multiline
          minRows={2}
        />
        <TextField
          fullWidth
          label="Other medical needs"
          value={fields.other_medical || ''}
          onChange={(e) => setF('other_medical', e.target.value)}
          margin="normal"
          size="small"
          multiline
          minRows={2}
        />
      </>
    );
  }
  if (formId === 'form_09') {
    const rows = fields.allergy_rows || [];
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {packet.student_first_name} {packet.student_last_name} — DOB {packet.student_dob || '—'}
        </Typography>
        {[0, 1, 2, 3, 4].map((i) => (
          <Box key={i} sx={{ mb: 2, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="caption">Allergen row {i + 1}</Typography>
            <TextField
              fullWidth
              size="small"
              label="Allergen / restriction"
              value={rows[i]?.allergen || ''}
              onChange={(e) => {
                const next = [...rows];
                next[i] = { ...next[i], allergen: e.target.value };
                setF('allergy_rows', next);
              }}
              margin="dense"
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {['eat', 'touch', 'smell'].map((k) => (
                <FormControlLabel
                  key={k}
                  control={
                    <Checkbox
                      checked={!!rows[i]?.[k]}
                      onChange={(e) => {
                        const next = [...rows];
                        next[i] = { ...next[i], [k]: e.target.checked };
                        setF('allergy_rows', next);
                      }}
                    />
                  }
                  label={k}
                />
              ))}
            </Box>
            <TextField
              fullWidth
              size="small"
              label="Reaction"
              value={rows[i]?.reaction || ''}
              onChange={(e) => {
                const next = [...rows];
                next[i] = { ...next[i], reaction: e.target.value };
                setF('allergy_rows', next);
              }}
              margin="dense"
            />
          </Box>
        ))}
        {row('Food from home (yes/no / notes)', 'food_from_home')}
        {row('Liquid type', 'liquid_type')}
      </>
    );
  }
  if (formId === 'form_10') {
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {packet.student_first_name} {packet.student_last_name} — DOB {packet.student_dob || '—'}
        </Typography>
        {row('Nickname', 'nickname')}
        {row('Hair color', 'hair_color')}
        {row('Eye color', 'eye_color')}
        {row('Glasses (yes/no)', 'glasses')}
        {row('Physical description', 'physical_description', { multiline: true, minRows: 3 })}
        {row('Usual behavior', 'usual_behavior', { multiline: true, minRows: 2 })}
        {row('Communication ability', 'communication_ability', { multiline: true, minRows: 2 })}
        {row('Favorite locations', 'favorite_locations')}
      </>
    );
  }
  if (formId === 'form_11' || formId === 'form_12') {
    return (
      <Typography variant="body2" color="text.secondary">
        {packet.student_first_name} {packet.student_last_name} — DOB {packet.student_dob || '—'}
        <br />
        Pediatrician section will be completed separately (Phase 6).
      </Typography>
    );
  }
  if (formId === 'form_13') {
    const items = [
      ['c_classroom', 'Classroom use (projects, Smartboards, bulletin boards)'],
      ['c_newsletters', 'Newsletters to classmates'],
      ['c_hallways', 'Photo/video in hallways'],
      ['c_website', 'Website / informational / fundraising'],
      ['c_flyers', 'Brochures / flyers'],
      ['c_slideshow_building', 'Slideshow in school building'],
      ['c_class_list', 'Name on class list'],
      ['c_yearbook', 'End-of-year slideshow / yearbook'],
    ];
    const commItems = [
      [
        'comm_sms',
        'I consent to receive text messages (SMS) from HASC/Gan Ezra regarding my child\'s program, including updates, reminders, and important notices.',
      ],
      [
        'comm_email',
        'I consent to receive emails from HASC/Gan Ezra regarding my child\'s program, including updates, reminders, and important notices.',
      ],
      [
        'comm_voice',
        'I consent to receive automated voice calls from HASC/Gan Ezra regarding my child\'s program, including updates, reminders, and important notices.',
      ],
      [
        'comm_data_rates_ack',
        'I understand that message and data rates may apply for text messages, and I may opt out at any time by replying STOP.',
      ],
    ];
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {packet.student_first_name} {packet.student_last_name} — DOB {packet.student_dob || '—'}
        </Typography>
        {items.map(([k, lab]) => (
          <FormControlLabel
            key={k}
            control={<Checkbox checked={!!fields[k]} onChange={(e) => setF(k, e.target.checked)} />}
            label={lab}
          />
        ))}
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          Communication Consent
        </Typography>
        {commItems.map(([k, lab]) => (
          <FormControlLabel
            key={k}
            sx={{ alignItems: 'flex-start', mr: 0 }}
            control={
              <Checkbox
                checked={!!fields[k]}
                onChange={(e) => setF(k, e.target.checked)}
                sx={{ pt: 0.25 }}
              />
            }
            label={<Typography variant="body2">{lab}</Typography>}
          />
        ))}
      </>
    );
  }
  if (formId === 'form_14') {
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Child: {packet.student_first_name} {packet.student_last_name} — Parent: {packet.parent_name || '—'}
        </Typography>
        <Typography variant="subtitle2">Authorized pickup 1</Typography>
        {row('Name', 'p1_name')}
        {row('Phone', 'p1_phone')}
        <Typography variant="subtitle2" sx={{ mt: 1 }}>Authorized pickup 2</Typography>
        {row('Name', 'p2_name')}
        {row('Phone', 'p2_phone')}
        <Typography variant="subtitle2" sx={{ mt: 1 }}>Authorized pickup 3</Typography>
        {row('Name', 'p3_name')}
        {row('Phone', 'p3_phone')}
      </>
    );
  }
  if (formId === 'form_15') {
    return (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {packet.student_first_name} {packet.student_last_name} — {packet.parent_name || '—'}
        </Typography>
        {row('Address', 'address', { multiline: true, minRows: 2 })}
        <FormControlLabel
          control={<Checkbox checked={!!fields.confirm_release} onChange={(e) => setF('confirm_release', e.target.checked)} />}
          label="I authorize the release described in the form above"
          sx={{ mt: 2 }}
        />
      </>
    );
  }
  if (formId === 'form_18' || formId === 'form_19') {
    return (
      <>
        <Typography variant="body2" color="text.secondary">
          {packet.student_first_name} {packet.student_last_name}
          {formId === 'form_19' && ` — ${packet.parent_name || ''}`}
          {packet.student_dob && ` — DOB ${packet.student_dob}`}
        </Typography>
        <FormControlLabel
          control={<Checkbox checked={!!fields.confirm_policy} onChange={(e) => setF('confirm_policy', e.target.checked)} />}
          label={formId === 'form_18' ? 'I have read and understand the illness policy' : 'I have reviewed the parent handbook'}
          sx={{ mt: 2 }}
        />
      </>
    );
  }
  return null;
}

export default function ParentPacketPage() {
  const { token } = useParams();
  const [packet, setPacket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [fields, setFields] = useState({});

  const fetchPacket = useCallback(async () => {
    if (!token) return;
    setError('');
    setLoading(true);
    try {
      const url = `${API_BASE_URL}/packet/${encodeURIComponent(token)}/`;
      const res = await fetch(url);
      if (!res.ok) {
        if (res.status === 404) throw new Error('invalid');
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to load');
      }
      const data = await res.json();
      setPacket(data);
      const firstPending = (data.forms || []).findIndex((f) => f.status !== 'signed');
      setCurrentIndex(firstPending >= 0 ? firstPending : (data.forms || []).length);
    } catch (e) {
      setPacket(null);
      setError(e.message === 'invalid' ? null : e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPacket();
  }, [fetchPacket]);

  const currentForm = packet?.forms?.[currentIndex];
  const formId = currentForm?.form_id;
  const savedFormDataKey =
    currentForm?.saved_form_data != null && typeof currentForm.saved_form_data === 'object'
      ? JSON.stringify(currentForm.saved_form_data)
      : '';

  useEffect(() => {
    if (!packet || !formId || currentForm?.status === 'signed') return;
    const init = initialFieldsForForm(formId, packet);
    if (savedFormDataKey) {
      try {
        const saved = JSON.parse(savedFormDataKey);
        if (saved && typeof saved === 'object') {
          setFields({ ...init, ...saved, confirm_agreed: false });
          return;
        }
      } catch {
        /* ignore */
      }
    }
    setFields(init);
  }, [packet, formId, currentIndex, currentForm?.status, savedFormDataKey]);

  const setF = useCallback((k, v) => {
    setFields((prev) => ({ ...prev, [k]: v }));
  }, []);

  const hasParentFields = currentForm?.has_parent_fields === true;

  const canSubmit = useMemo(() => {
    if (!currentForm || currentForm.status === 'signed') return true;
    if (!hasParentFields) return true;
    if (formId === 'form_11' || formId === 'form_12') return !!fields.confirm_agreed;
    if (formId === 'form_15') return !!fields.confirm_release && !!fields.confirm_agreed;
    if (formId === 'form_18' || formId === 'form_19') return !!fields.confirm_policy && !!fields.confirm_agreed;
    return !!fields.confirm_agreed;
  }, [currentForm, hasParentFields, formId, fields]);

  const buildFormPayload = () => {
    const { confirm_agreed, ...rest } = fields;
    return rest;
  };

  const handleSubmitForm = async () => {
    if (!packet || !packet.forms || submitting) return;
    const form = packet.forms[currentIndex];
    if (!form || form.status === 'signed') {
      setCurrentIndex((i) => Math.min(i + 1, packet.forms.length));
      return;
    }
    if (hasParentFields && !canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const body = hasParentFields ? { form_data: buildFormPayload() } : { form_data: {} };
      const url = `${API_BASE_URL}/packet/${encodeURIComponent(token)}/${encodeURIComponent(form.form_id)}/submit/`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to submit');
      }
      const updated = await fetch(`${API_BASE_URL}/packet/${encodeURIComponent(token)}/`).then((r) => r.json());
      setPacket(updated);
      setCurrentIndex((i) => Math.min(i + 1, packet.forms.length));
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !packet) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', p: 3, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          This link is invalid or has expired. Please contact the school.
        </Alert>
      </Box>
    );
  }

  if (!packet || !packet.forms || packet.forms.length === 0) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', p: 3 }}>
        <Typography>No forms to display.</Typography>
      </Box>
    );
  }

  const total = packet.forms.length;
  const allSigned = packet.forms.every((f) => f.status === 'signed');

  if (allSigned || currentIndex >= total) {
    const printUrl = `${API_BASE_URL}/packet/${encodeURIComponent(token)}/print/`;
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', p: 3, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ mb: 2, fontSize: '1.5rem' }}>
          Thank you!
        </Typography>
        <Typography sx={{ fontSize: '1.1rem', mb: 3 }}>
          All forms have been submitted.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="large"
          href={printUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ py: 1.5, px: 3, fontSize: '1rem' }}
        >
          Print / Save My Packet
        </Button>
      </Box>
    );
  }

  const pdfUrl = `${API_BASE_URL}/packet/${encodeURIComponent(token)}/${encodeURIComponent(currentForm.form_id)}/pdf/`;
  const submitLabel = hasParentFields ? 'Submit & continue' : 'Mark as reviewed & continue';
  const informational = isInformationalPacketForm(formId);
  const textOnlyInfo = informational && informationalTextOnlyForm(formId);
  const pdfInfo = informational && informationalPdfForm(formId);

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', p: 2, minHeight: '80vh' }}>
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Typography variant="h6" sx={{ mb: 1, fontSize: '1.1rem' }}>
        {packet.student_first_name && `Hi ${packet.student_first_name}, `}
        Parent Packet — {packet.school_year}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Form {currentIndex + 1} of {total}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={((currentIndex + 1) / total) * 100}
        sx={{ mb: 2, height: 8, borderRadius: 1 }}
      />
      <Typography variant="h6" sx={{ mb: 1.5, fontSize: '1.25rem' }}>
        {currentForm.form_name}
      </Typography>

      {textOnlyInfo && (
        <Paper sx={{ p: 3, mb: 3 }}>
          {currentForm.status === 'signed' ? (
            <>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                You have already completed this form.
              </Typography>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleSubmitForm}
                disabled={submitting}
                sx={{ py: 1.5, fontSize: '1rem' }}
              >
                Continue
              </Button>
            </>
          ) : (
            <>
              <Box
                sx={{
                  bgcolor: '#fff',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  maxHeight: 400,
                  overflowY: 'auto',
                  mb: 3,
                  fontSize: '14px',
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                  color: 'text.primary',
                }}
              >
                {currentForm.form_text || 'Loading…'}
              </Box>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleSubmitForm}
                disabled={submitting || !canSubmit}
                sx={{ py: 1.5, fontSize: '1rem' }}
              >
                {submitting ? 'Submitting…' : submitLabel}
              </Button>
            </>
          )}
        </Paper>
      )}

      {pdfInfo && (
        <Paper sx={{ p: 2, mb: 3 }}>
          {currentForm.status === 'signed' ? (
            <>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                You have already completed this form.
              </Typography>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleSubmitForm}
                disabled={submitting}
                sx={{ py: 1.5, fontSize: '1rem' }}
              >
                Continue
              </Button>
            </>
          ) : (
            <>
              {PDF_INFO_HINTS[formIdToNum(formId)] && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {PDF_INFO_HINTS[formIdToNum(formId)]}
                </Typography>
              )}
              <Box sx={{ width: '100%', minHeight: 480, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <iframe
                  title={`Form: ${currentForm.form_name}`}
                  src={pdfUrl}
                  style={{ width: '100%', minHeight: 480, height: '65vh', border: 'none' }}
                />
              </Box>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleSubmitForm}
                disabled={submitting || !canSubmit}
                sx={{ py: 1.5, fontSize: '1rem' }}
              >
                {submitting ? 'Submitting…' : submitLabel}
              </Button>
            </>
          )}
        </Paper>
      )}

      {/* Fillable forms: fields first, PDF below, submit last */}
      {!informational && (
        <>
          <Paper sx={{ p: 3, mb: 2 }}>
            {currentForm.status === 'signed' ? (
              <Typography color="text.secondary">You have already completed this form.</Typography>
            ) : (
              <>
                {hasParentFields && (
                  <>
                    <Typography sx={{ mb: 2, fontWeight: 600 }}>Your responses</Typography>
                    <FormFields formId={formId} fields={fields} setF={setF} packet={packet} />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!fields.confirm_agreed}
                          onChange={(e) => setF('confirm_agreed', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="I have read the form above and confirm my entries are accurate"
                      sx={{ mt: 2, display: 'block' }}
                    />
                  </>
                )}
                {!hasParentFields && (
                  <Typography sx={{ mb: 2 }}>
                    Review the form below, then continue.
                  </Typography>
                )}
              </>
            )}
          </Paper>

          <Box sx={{ width: '100%', minHeight: 500, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <iframe
              title={`Form: ${currentForm.form_name}`}
              src={pdfUrl}
              style={{ width: '100%', minHeight: 500, height: '70vh', border: 'none' }}
            />
          </Box>

          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleSubmitForm}
            disabled={submitting || (currentForm.status !== 'signed' && !canSubmit)}
            sx={{ mb: 3, py: 1.5, fontSize: '1rem' }}
          >
            {submitting ? 'Submitting…' : currentForm.status === 'signed' ? 'Continue' : submitLabel}
          </Button>
        </>
      )}
    </Box>
  );
}
